import { GuideSection } from '@/components/guides/GuideSection';
import { GuideSteps, GuideStep } from '@/components/guides/GuideSteps';
import { GuideCallout } from '@/components/guides/GuideCallout';

export function FirstTimeSetupSection() {
  return (
    <GuideSection
      id="first-time-setup"
      eyebrow="Step 2"
      title="First-time setup"
      description="Link the app to your team, give it the permissions it needs, and run a quick mic test. About 5 minutes."
    >
      <GuideSteps>
        <GuideStep number={1} title="Enter the company code">
          <p>
            On first launch, NeuraX asks for a <strong>company code</strong>. That's the short code
            in the form <code>XXXX-YYYY-ZZZZ</code> that your recruiter shared with you. Type or
            paste it exactly as shown — it's case-insensitive.
          </p>
          <p>This links your install to your team. You only do this once.</p>
        </GuideStep>

        <GuideStep number={2} title="Enter your email">
          <p>
            Use the <strong>same email address</strong> your recruiter used to invite you. That's
            how NeuraX matches you to the sessions your team has scheduled. Wrong email = no
            sessions, and you'll get a friendly error.
          </p>
        </GuideStep>

        <GuideStep number={3} title="Paste your license key">
          <p>
            When you were added to a session, your team sent you a <strong>license key</strong>
            like <code>NRX-XXXXXX-XXXXXXXX</code>. Paste it into the license field. NeuraX binds the
            key to this laptop on first successful activation.
          </p>
        </GuideStep>

        <GuideStep number={4} title="Grant permissions">
          <p>NeuraX will ask for:</p>
          <ul className="ml-4 list-disc space-y-1">
            <li>
              <strong>Microphone access</strong> — so it can hear the interview audio. Required.
            </li>
            <li>
              <strong>System audio / screen recording</strong> — so it can hear the interviewer too
              (their voice comes through the laptop speakers). Required for interviews; the setting
              lives in macOS{' '}
              <em>System Settings → Privacy & Security → Screen & System Audio Recording</em>.
            </li>
            <li>
              <strong>Notifications</strong> — optional, just lets the app tell you when a session
              is ready.
            </li>
          </ul>
          <p>
            If you deny microphone access, NeuraX can't coach during the interview. You can flip the
            permission on later in System Settings.
          </p>
        </GuideStep>

        <GuideStep number={5} title="Run the mic test">
          <p>
            NeuraX's setup flow ends with a 10-second mic test. Speak into the mic; a level bar
            moves. If it doesn't move, check that your mic is selected in the dropdown (AirPods,
            internal mic, USB headset) and try again.
          </p>
        </GuideStep>
      </GuideSteps>

      <GuideCallout variant="info" title="What 'bind to machine' means">
        <p>
          Your license key activates <strong>once</strong>, on the first laptop where you enter it.
          After that, the same key won't work on another machine until you ask your recruiter to
          reset the binding. This keeps licenses from being shared.
        </p>
      </GuideCallout>

      <GuideCallout variant="warning" title="Upgrading your laptop?">
        <p>
          Got a new machine between installs? Tell your recruiter. They can reset the machine
          binding in two clicks and you'll re-enter the same license on the new laptop — no new key
          needed.
        </p>
      </GuideCallout>
    </GuideSection>
  );
}
